import { Stack } from '@mantine/core';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import Toast from 'components/Toast';
import FilePreview from 'components/files/FilePreview';
import Slider from 'components/modals/slider/Slider';
import H3Medium from 'components/typography/H3Medium';
import { useCallback, useMemo, useState } from 'react';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * Parameters of the RevisionFile component.
 */
export interface RevisionFileProps {
  revision: RevisionGetResponse;
}

/**
 * Displays the revision PDF.
 */
export default function RevisionFile({ revision }: RevisionFileProps) {
  const { isTablet } = useResponsiveDesign();
  const [openedSlider, setOpenSlider] = useState(false);
  const [initialFileId, setInitialFileId] = useState('');

  const handleOpenSlider = useCallback(
    (fileId: string) => {
      setInitialFileId(fileId);
      setOpenSlider(true);
    },
    [revision]
  );

  const filePreview = useMemo(() => {
    if (revision.signedFileId) {
      return (
        <FilePreview
          key="signedFileId"
          fileId={revision.signedFileId}
          hideDelete
          excerptLength={100}
          showSlider
          withKep
          onOpenSlider={() => handleOpenSlider(revision.signedFileId!)}
        />
      );
    }

    if (revision.fileId && !revision.kepSignable) {
      return (
        <FilePreview
          key="fileId"
          fileId={revision.fileId}
          hideDelete
          excerptLength={100}
          showSlider
          onOpenSlider={() => handleOpenSlider(revision.fileId!)}
        />
      );
    }

    return (
      <Toast
        type="warning"
        message={
          revision.kind === 'revision'
            ? 'PDF súbor revíznej správy nie je dostupný'
            : 'PDF súbor formulára nie je dostupný'
        }
        withCloseButton={false}
        fullWidth
      />
    );
  }, [revision]);

  const archiveClausePreview = useMemo(() => {
    if (revision.archiveClauseFileId) {
      return (
        <FilePreview
          key="archiveClauseFileId"
          fileId={revision.archiveClauseFileId}
          hideDelete
          excerptLength={100}
          showSlider
          onOpenSlider={() => handleOpenSlider(revision.archiveClauseFileId!)}
        />
      );
    }

    return null;
  }, [revision]);

  return (
    <Stack spacing={24} py={24} px={isTablet ? 20 : 40} style={{ borderRadius: '4px' }} bg="white">
      <H3Medium color="gray.7">{revision.kind === 'revision' ? 'Revízna správa' : 'Formulár'}</H3Medium>
      {filePreview}
      {archiveClausePreview}
      <Slider
        key={initialFileId}
        opened={openedSlider}
        fileIds={[initialFileId]}
        onClose={() => setOpenSlider(false)}
        initialImageId={initialFileId}
        hideControls={true}
      />
    </Stack>
  );
}
